/**
 * Analytics tracking wrapper with guard protection
 * All GA4 tracking must go through these functions to respect user privacy and prevent dev pollution
 */

import ReactGA from "react-ga4";
import { shouldTrackPageViews, shouldTrackEvents, getDebugConfig } from './guard';
import { hasConsentFor, COOKIE_CATEGORIES } from '../utils/cookieConsent';
import { validateEventParams, createStandardContext } from './events';

const GA_TRACKING_ID = "G-F351N4EDYQ";

/**
 * Initialize Google Analytics with guard protection
 * Initializes if basic pageview tracking is allowed (production, valid domain, etc.)
 * Uses minimal tracking mode if no consent, full tracking if consent given
 */
export const initGA = () => {
  // Check if we can do basic pageview tracking
  if (!shouldTrackPageViews()) {
    console.debug('Analytics initialization blocked by pageview guard');
    return;
  }

  try {
    const debugConfig = getDebugConfig();
    const hasConsent = hasConsentFor(COOKIE_CATEGORIES.ANALYTICS);
    
    // Initialize with appropriate settings based on consent
    const gaOptions = {
      anonymize_ip: true, // Always anonymize IPs for privacy
      cookie_flags: 'SameSite=Strict;Secure',
      ...debugConfig
    };

    // If no consent, use more restrictive settings
    if (!hasConsent) {
      gaOptions.storage = 'none'; // Disable analytics storage without consent
      gaOptions.clientId = 'anonymous'; // Use anonymous client ID
      console.debug('Google Analytics initialized in minimal tracking mode (no consent)');
    } else {
      console.debug('Google Analytics initialized with full tracking (consent given)');
    }
    
    ReactGA.initialize(GA_TRACKING_ID, { gaOptions });
    
  } catch (error) {
    console.error('Failed to initialize Google Analytics:', error);
  }
};

/**
 * Track page view with guard protection  
 * Now allows basic pageview tracking without consent for better analytics
 * @param {string} path - Page path to track
 * @param {object} configContext - Config from React app (optional)
 */
export const trackPageView = (path, configContext = {}) => {
  // Only need basic pageview guard - no consent required
  if (!shouldTrackPageViews()) {
    console.debug('Page view tracking blocked:', path);
    return;
  }

  try {
    const hasConsent = hasConsentFor(COOKIE_CATEGORIES.ANALYTICS);
    
    // Create basic or enhanced context based on consent
    const context = hasConsent 
      ? createStandardContext(configContext)  // Full context with consent
      : { page_title: document.title };       // Minimal context without consent
    
    ReactGA.send({ 
      hitType: "pageview", 
      page: path,
      custom_parameters: context
    });
    
    console.debug('Page view tracked:', path, { 
      consentLevel: hasConsent ? 'full' : 'minimal',
      context 
    });
  } catch (error) {
    console.error('Failed to track page view:', error);
  }
};

/**
 * Track event with guard protection and validation
 * Requires explicit user consent for analytics cookies
 * @param {string} category - Event category
 * @param {string} action - Event action  
 * @param {string|object} label - Event label (legacy) or parameters object (new)
 * @param {object} configContext - Config from React app (optional)
 */
export const trackEvent = (category, action, label = "", configContext = {}) => {
  const hasConsent = hasConsentFor(COOKIE_CATEGORIES.ANALYTICS);
  
  // Events require full consent - use shouldTrackEvents with consent parameter
  if (!shouldTrackEvents(hasConsent)) {
    console.debug('Event tracking blocked:', category, action, label, {
      reason: hasConsent ? 'environment/domain check failed' : 'no user consent'
    });
    return;
  }

  try {
    let eventParams = {};
    
    // Handle legacy string labels vs new object parameters
    if (typeof label === 'string') {
      eventParams = { label };
    } else if (typeof label === 'object' && label !== null) {
      eventParams = { ...label };
    }
    
    // Add standard context
    const context = createStandardContext(configContext);
    eventParams = { ...eventParams, ...context };
    
    // Validate parameters
    const validation = validateEventParams(category, eventParams);
    if (!validation.isValid) {
      console.warn('Event validation warnings:', validation.errors);
      // Continue tracking despite warnings (non-blocking)
    }
    
    ReactGA.event({
      category,
      action,
      ...eventParams
    });
    
    console.debug('Event tracked:', category, action, eventParams);
  } catch (error) {
    console.error('Failed to track event:', error);
  }
};

/**
 * Track home search events with salary range binning (legacy compatibility)
 * @param {Object} params - Search parameters
 * @param {string} params.salaryRange - Binned salary range for privacy
 * @param {object} configContext - Config from React app (optional)
 */
export const trackHomeSearch = ({ salaryRange }, configContext = {}) => {
  trackEvent('Home Search', 'Submit', { salaryRange }, configContext);
};

/**
 * Track trust badge clicks (legacy compatibility)
 * @param {string} badge - Badge name that was clicked
 * @param {object} configContext - Config from React app (optional)
 */
export const trackBadgeClick = (badge, configContext = {}) => {
  trackEvent('Trust Badge', 'Click', badge, configContext);
};

/**
 * Track CTA clicks with surface context (legacy compatibility)
 * @param {Object} params - CTA parameters
 * @param {string} params.surface - CTA surface ('hero', 'sticky', etc.)
 * @param {object} configContext - Config from React app (optional)
 */
export const trackCTA = ({ surface }, configContext = {}) => {
  trackEvent('CTA', 'Click', surface, configContext);
};

/**
 * Convert salary to privacy-safe range bins
 * @param {number} salary - Raw salary amount
 * @returns {string} - Salary range bin (e.g., "25k-50k")
 */
export const getSalaryRangeBin = (salary) => {
  if (salary < 20000) return 'under-20k';
  if (salary < 25000) return '20k-25k';
  if (salary < 30000) return '25k-30k';
  if (salary < 40000) return '30k-40k';
  if (salary < 50000) return '40k-50k';
  if (salary < 75000) return '50k-75k';
  if (salary < 100000) return '75k-100k';
  if (salary < 150000) return '100k-150k';
  if (salary < 200000) return '150k-200k';
  if (salary < 300000) return '200k-300k';
  if (salary < 500000) return '300k-500k';
  return 'over-500k';
};

// Re-export guard functions for debugging
export { shouldTrackPageViews, shouldTrackEvents, shouldTrack, getDebugConfig, getTrackingStatus, clearStaffTestingFlag } from './guard';